/**
 * FolderDetailPage — per-product workspace (editorial layout).
 *
 * TOP BANNER (full width): product info — LEFT: name (≤50), description, category,
 *   price, pain point, editable feature & benefit pills; RIGHT: images (one large
 *   main thumbnail + smaller thumbnails underneath). A tidy TikTok Shop import row
 *   turns into a "View product ↗" link + bin once a product is imported.
 * Below: four full-width numbered ACCORDIONS —
 *   1 Viral scripts
 *   Winning pattern (standalone, under Top creator videos)
 *   2 Top creator videos
 *   3 History
 *   4 Write my script (tone + a required radio choice + one button) — open by default.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import productCategories from '@/lib/productCategories';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  ArrowLeft, Trash2, Plus, Loader2, RefreshCw, ChevronDown, ChevronUp,
  ExternalLink, AlertCircle, Copy, ArrowRight, X, Sparkles, Clapperboard,
} from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import AppShell from '@/components/AppShell';
import VoiceoverButton from '@/components/VoiceoverButton';

const API = process.env.REACT_APP_BACKEND_URL;
const NAME_MAX = 50;

// Timeline segment colours (pink → dark → mid → light → teal).
const PHASE_COLOURS = ['#BE185D', '#1F1B16', '#8B8178', '#C7BDB0', '#0B5566'];

const parseSpan = (t) => {
  const m = String(t || '').match(/(\d+)\s*-\s*(\d+)/);
  if (m) return Math.max(1, parseInt(m[2], 10) - parseInt(m[1], 10));
  return 1;
};

const videoHandle = (t) => {
  if (t.meta?.handle) return `@${t.meta.handle}`;
  const m = String(t.url || '').match(/@([\w.]+)/);
  return m ? `@${m[1]}` : 'Viral video';
};
const videoRef = (t) => {
  const m = String(t.url || '').match(/(?:video|reel|p)\/(\w+)/);
  if (m) return `video/${m[1].slice(0, 10)}…`;
  return t.url;
};

const FolderDetailPage = () => {
  const { id } = useParams();
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();

  const [folder, setFolder] = useState(null);
  const [loading, setLoading] = useState(true);

  const [transcriptUrl, setTranscriptUrl] = useState('');
  const [addingTranscript, setAddingTranscript] = useState(false);
  const [openTranscriptId, setOpenTranscriptId] = useState(null);
  const [showManual, setShowManual] = useState(false);
  const [manualText, setManualText] = useState('');
  const [savingManual, setSavingManual] = useState(false);
  const [importUrl, setImportUrl] = useState('');
  const [importing, setImporting] = useState(false);
  const [resettingUrl, setResettingUrl] = useState(false);
  const [findingCreators, setFindingCreators] = useState(false);
  const [usingCreator, setUsingCreator] = useState(null);
  const [creatorError, setCreatorError] = useState('');

  const [analyzing, setAnalyzing] = useState(false);
  const [analyzingCreators, setAnalyzingCreators] = useState(false);
  const [usingTopCreators, setUsingTopCreators] = useState(false);

  // Product details
  const [productName, setProductName] = useState('');
  const [productCategory, setProductCategory] = useState('');
  const [productPrice, setProductPrice] = useState('');
  const [productPain, setProductPain] = useState('');
  const [productDescription, setProductDescription] = useState('');
  const [benefits, setBenefits] = useState([]);
  const [benefitDraft, setBenefitDraft] = useState('');
  const [addingBenefit, setAddingBenefit] = useState(false);
  const [editingBenefitIdx, setEditingBenefitIdx] = useState(null);
  const [editBenefitDraft, setEditBenefitDraft] = useState('');
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [activeImageIdx, setActiveImageIdx] = useState(0);

  const [generating, setGenerating] = useState(false);
  const [generatingFlavour, setGeneratingFlavour] = useState(null);
  const [lastFlavour, setLastFlavour] = useState(null);
  const [lastScriptType, setLastScriptType] = useState(null);

  const [scriptStyle, setScriptStyle] = useState('ugc');
  const [videoCategory, setVideoCategory] = useState('sales');
  const [hookIngredient, setHookIngredient] = useState('');
  const [appliedTo, setAppliedTo] = useState('');
  const [genMode, setGenMode] = useState('mof'); // bof | mof | tof | winning
  const [scriptGuidance, setScriptGuidance] = useState('');
  const [showGuidance, setShowGuidance] = useState(false);
  const [voiceoverText, setVoiceoverText] = useState('');
  const [voiceAffiliates, setVoiceAffiliates] = useState({ elevenlabs: '', fish: '' });
  const includeExtras = false;

  const [activeGen, setActiveGen] = useState(null);
  const [filmingByGen, setFilmingByGen] = useState({});
  const [filmingLoading, setFilmingLoading] = useState(false);
  const [openPattern, setOpenPattern] = useState({ hook_style: true, structure: true });
  // Which numbered accordions are open (Create script open by default).
  const [openSections, setOpenSections] = useState({ winning: false, creators: false, write: true, voiceover: false, history: false });

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/folders/${id}`, { headers: getAuthHeader() });
      setFolder(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load folder');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [id, getAuthHeader, navigate]);

  useEffect(() => { load(); }, [load]);

  // Affiliate sign-up links shown in the voiceover section.
  useEffect(() => {
    axios.get(`${API}/api/voiceover/providers`)
      .then((res) => {
        const map = {};
        (res.data.providers || []).forEach((p) => { map[p.key] = p.affiliate_url; });
        setVoiceAffiliates((prev) => ({ ...prev, ...map }));
      })
      .catch(() => { /* keep fallbacks */ });
  }, []);

  useEffect(() => {
    if (!folder) return;
    setProductName((v) => v || (folder.name || '').slice(0, NAME_MAX));
    setProductCategory((v) => v || folder.category || '');
    setProductPrice((v) => v || folder.price || '');
    setProductPain((v) => v || folder.pain_points || '');
    setProductDescription((v) => v || folder.product_description || '');
    setBenefits((v) => (v.length ? v : (folder.benefits || [])));
    if (folder.platform === 'instagram') setGenMode((m) => (m === 'winning' ? m : 'tof'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folder?.id]);

  if (loading || !folder) {
    return (
      <div className="min-h-screen bg-[#F4EFE7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const platform = folder.platform || 'tiktok';
  const isInstagram = platform === 'instagram';
  const platformLabel = isInstagram ? 'Instagram' : 'TikTok';
  const isGrowth = isInstagram && videoCategory === 'growth';
  const transcriptLimit = folder.limits?.transcripts_per_folder ?? 3;
  const atTranscriptLimit = (folder.transcripts?.length || 0) >= transcriptLimit;
  const hasTranscripts = (folder.transcripts?.length || 0) > 0;
  const structured = folder.analysis_structured;
  const patternVideoCount = folder.analysis_video_count || folder.transcripts?.length || 0;
  const linkNoun = isInstagram ? 'Instagram link' : 'TikTok link';
  const creatorVideos = folder.creator_videos || [];
  const productUrl = folder.product_url || '';
  const productImages = folder.product_images || [];
  const mainImage = productImages[activeImageIdx] || productImages[0];
  const addedVideoIds = new Set(
    (folder.transcripts || [])
      .map((t) => videoRef(t))
      .concat((folder.transcripts || []).map((t) => String(t.url || '')))
  );
  const fmtCount = (n) => {
    const v = Number(n);
    if (!v || Number.isNaN(v)) return null;
    if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
    if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
    return `${v}`;
  };

  const toggleSection = (key) => setOpenSections((s) => ({ ...s, [key]: !s[key] }));

  const addTranscript = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const urls = transcriptUrl
      .split(/[\n,\s]+/)
      .map((u) => u.trim())
      .filter((u) => /^https?:\/\//i.test(u));
    if (urls.length === 0) { toast.error('Paste at least one valid link'); return; }
    const remaining = Math.max(0, transcriptLimit - (folder.transcripts?.length || 0));
    if (remaining === 0) { toast.error(`You've hit the ${transcriptLimit}-video limit`); return; }
    const toAdd = urls.slice(0, remaining);
    setAddingTranscript(true);
    let ok = 0;
    const failed = [];
    for (const url of toAdd) {
      try {
        await axios.post(`${API}/api/folders/${id}/transcripts`, { url }, { headers: getAuthHeader() });
        ok += 1;
      } catch (err) {
        failed.push(err.response?.data?.detail || url);
      }
    }
    if (ok) toast.success(`Saved ${ok} video${ok > 1 ? 's' : ''}${ok >= 2 ? ' — now hit Analyse' : ''}`);
    if (failed.length) toast.error(`${failed.length} link${failed.length > 1 ? 's' : ''} couldn't be added`);
    if (urls.length > toAdd.length) toast.message(`Only ${remaining} slot(s) left — extra links skipped`);
    setTranscriptUrl('');
    await load();
    setAddingTranscript(false);
  };

  const saveManualTranscript = async () => {
    if (!manualText.trim()) return;
    setSavingManual(true);
    try {
      await axios.post(`${API}/api/folders/${id}/transcripts`, { transcript: manualText.trim() }, { headers: getAuthHeader() });
      toast.success('Transcript saved');
      setManualText('');
      setShowManual(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save transcript');
    } finally {
      setSavingManual(false);
    }
  };

  const saveProductDetails = async () => {
    try {
      await axios.patch(`${API}/api/folders/${id}`, {
        category: productCategory.trim(),
        price: productPrice.trim(),
        pain_points: productPain.trim(),
        product_description: productDescription.trim(),
        benefits: benefits.map((b) => b.trim()).filter(Boolean),
      }, { headers: getAuthHeader() });
    } catch { /* non-blocking */ }
  };

  const importProduct = async () => {
    if (!importUrl.trim()) return;
    setImporting(true);
    try {
      const res = await axios.post(`${API}/api/folders/${id}/import-product`, { url: importUrl.trim() }, { headers: getAuthHeader() });
      const d = res.data || {};
      if (d.title) {
        setProductName(d.title.slice(0, NAME_MAX));
        try { await axios.patch(`${API}/api/folders/${id}`, { name: d.title.slice(0, NAME_MAX) }, { headers: getAuthHeader() }); } catch { /* non-blocking */ }
      }
      if (d.category) setProductCategory(d.category);
      if (d.price) setProductPrice(d.price);
      if (d.pain_points) setProductPain(d.pain_points);
      if (d.product_description) setProductDescription(d.product_description);
      if (Array.isArray(d.benefits) && d.benefits.length) setBenefits(d.benefits.slice(0, 5));
      setActiveImageIdx(0);
      toast.success('Product details imported');
      setImportUrl('');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not import that product');
    } finally {
      setImporting(false);
    }
  };

  const resetProductUrl = async () => {
    if (!window.confirm('Clear the TikTok Shop link (and its imported images)? Your typed details stay.')) return;
    setResettingUrl(true);
    try {
      await axios.patch(`${API}/api/folders/${id}`, { product_url: '' }, { headers: getAuthHeader() });
      setCreatorError('');
      setActiveImageIdx(0);
      toast.success('Product link cleared');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not clear the link');
    } finally {
      setResettingUrl(false);
    }
  };

  const deleteTranscript = async (transcriptId) => {
    if (!window.confirm('Remove this viral video?')) return;
    try {
      await axios.delete(`${API}/api/folders/${id}/transcripts/${transcriptId}`, { headers: getAuthHeader() });
      toast.success('Video removed');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove');
    }
  };

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      await axios.post(`${API}/api/folders/${id}/analyze`, {}, { headers: getAuthHeader() });
      toast.success('Winning pattern extracted');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const analyzeCreatorScripts = async () => {
    setAnalyzingCreators(true);
    try {
      toast.info('Pulling in this product’s top 3 videos and transcribing them…');
      await axios.post(`${API}/api/folders/${id}/analyze-creator-videos`, {}, { headers: getAuthHeader(), timeout: 180000 });
      toast.success('Top 3 videos pulled in as viral scripts');
    } catch (err) {
      // The transcripts are saved before the (optional) pattern analysis runs,
      // so refresh regardless — they'll still appear in Viral scripts.
      toast.error(err.response?.data?.detail || 'Could not pull in the top videos');
    } finally {
      await load();
      setAnalyzingCreators(false);
    }
  };

  const runGenerate = async (withInspiration, scriptType = null) => {
    if (!productName.trim()) {
      toast.error('Give the product a name'); return;
    }
    setGenerating(true);
    setGeneratingFlavour(scriptType ? `quick-${scriptType}` : (withInspiration ? 'inspired' : 'fresh'));
    setLastFlavour(withInspiration ? 'inspired' : 'fresh');
    setLastScriptType(scriptType);
    try {
      const payload = {
        product_name: productName.trim(),
        category: productCategory.trim(),
        price: productPrice.trim(),
        pain_points: productPain.trim(),
        benefits: benefits.map((b) => b.trim()).filter(Boolean),
        folder_id: id,
        platform,
        script_style: scriptStyle,
        video_category: videoCategory,
        hook_ingredient: hookIngredient.trim() || null,
        applied_to: appliedTo.trim() || null,
        include_extras: platform === 'tiktok' && scriptType !== 'bof' ? includeExtras : false,
      };
      if (scriptType) payload.script_type = scriptType;
      if (scriptGuidance.trim()) payload.script_note = scriptGuidance.trim();
      if (!withInspiration) payload.inspiration_transcript = ' ';
      await saveProductDetails();
      const res = await axios.post(`${API}/api/generate-content`, payload, { headers: getAuthHeader() });
      toast.success(scriptType ? 'Quick script ready' : (withInspiration ? 'Script written from the winning pattern' : 'Fresh script written'));
      window.dispatchEvent(new Event('usage-updated'));
      if (res.data) { setActiveGen(res.data); setVoiceoverText(spokenClean(res.data)); }
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
      setGeneratingFlavour(null);
    }
  };

  // One button → generate according to the selected radio.
  const handleWriteScript = () => {
    if (genMode === 'winning') return runGenerate(true);
    if (isInstagram) return runGenerate(false);
    return runGenerate(false, genMode); // bof | tof | general (TikTok)
  };

  const findCreatorVideos = async () => {
    setFindingCreators(true);
    setCreatorError('');
    try {
      const res = await axios.post(`${API}/api/folders/${id}/creator-videos`, {}, { headers: getAuthHeader() });
      const vids = res.data?.creator_videos || [];
      if (vids.length) {
        toast.success(`Found ${vids.length} creator video${vids.length === 1 ? '' : 's'}`);
      } else {
        setCreatorError('No creator videos found for this product.');
      }
      await load();
    } catch (err) {
      setCreatorError(err.response?.data?.detail || 'Could not find creator videos.');
    } finally {
      setFindingCreators(false);
    }
  };

  const addCreatorVideoAsInspiration = async (url) => {
    setUsingCreator(url);
    try {
      await axios.post(`${API}/api/folders/${id}/transcripts`, { url }, { headers: getAuthHeader() });
      toast.success('Added to viral scripts');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not add that video');
    } finally {
      setUsingCreator(null);
    }
  };

  const pullTopCreatorVideos = async () => {
    const top = (folder.creator_videos || []).slice(0, 3).filter((v) => v && v.url);
    if (!top.length) return;
    setUsingTopCreators(true);
    let added = 0;
    let attempted = 0;
    try {
      for (const v of top) {
        const remaining = Math.max(0, transcriptLimit - ((folder.transcripts?.length || 0) + added));
        if (remaining === 0) break;
        if (addedVideoIds.has(String(v.url))) continue;
        attempted += 1;
        try {
          await axios.post(`${API}/api/folders/${id}/transcripts`, { url: v.url }, { headers: getAuthHeader() });
          added += 1;
        } catch (e) { /* skip videos that fail to transcribe */ }
      }
      if (added) {
        const newTotal = (folder.transcripts?.length || 0) + added;
        if (newTotal >= 2) {
          toast.success(`Added ${added} video${added > 1 ? 's' : ''} to viral scripts`, {
            action: { label: 'Analyse these now', onClick: () => runAnalysis() },
            duration: 10000,
          });
        } else {
          toast.success(`Added ${added} video to viral scripts. Add 1 more to analyse.`);
        }
      }
      else if (attempted > 0) toast.error('Could not transcribe those videos. Try adding one manually.');
      else toast.info(atTranscriptLimit ? 'Viral-scripts limit reached' : 'Those videos are already in your viral scripts');
      await load();
    } finally {
      setUsingTopCreators(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied');
  };

  const dialogueOnly = (g) => {
    const parts = [];
    if (g?.opening_line) parts.push(g.opening_line);
    (g?.scenes || []).forEach((sc) => { if (sc?.dialogue) parts.push(sc.dialogue); });
    return parts.join('\n\n').trim();
  };

  // Strip everything that isn't spoken script: markdown, (stage directions),
  // [brackets], hook/overlay/timestamp labels. Used for the script display and
  // for what gets passed to the voiceover.
  const cleanScriptText = (text) => {
    if (!text) return '';
    return String(text)
      .replace(/\*\*/g, '')
      .replace(/\([^)]*\)/g, '')
      .replace(/\[[^\]]*\]/g, '')
      .replace(/^\s*(hook|on[-\s]?screen\s*text|visual|overlay|cta|caption|text|scene\s*\d*)\s*[:\-–].*$/gim, '')
      .replace(/\b\d{1,2}[:.]\d{2}\s*[-–]\s*\d+\s*seconds?\b/gi, '')
      .replace(/[^\S\n]+\n/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  };

  const spokenClean = (g) => (g?.scenes?.length ? dialogueOnly(g) : cleanScriptText(g?.script || ''));

  const generateFilming = async (g) => {
    setFilmingLoading(true);
    try {
      const res = await axios.post(`${API}/api/generate-filming`, {
        product_name: productName.trim(),
        category: productCategory.trim(),
        price: productPrice.trim(),
        pain_points: productPain.trim(),
        benefits: benefits.map((b) => b.trim()).filter(Boolean),
        folder_id: id,
        platform,
      }, { headers: getAuthHeader() });
      const ideas = res.data?.filming_ideas || [];
      setFilmingByGen((m) => ({ ...m, [g.id]: ideas }));
      window.dispatchEvent(new Event('usage-updated'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not generate filming angles');
    } finally {
      setFilmingLoading(false);
    }
  };

  const commitBenefit = () => {
    const v = benefitDraft.trim();
    if (v && benefits.length < 5) setBenefits((prev) => [...prev, v]);
    setBenefitDraft('');
    setAddingBenefit(false);
  };
  const removeBenefit = (idx) => setBenefits((prev) => prev.filter((_, i) => i !== idx));
  const startEditBenefit = (idx) => { setEditingBenefitIdx(idx); setEditBenefitDraft(benefits[idx] || ''); };
  const commitBenefitEdit = () => {
    const v = editBenefitDraft.trim();
    setBenefits((prev) => prev.map((b, i) => (i === editingBenefitIdx ? (v || b) : b)));
    setEditingBenefitIdx(null);
    setEditBenefitDraft('');
  };

  const togglePattern = (key) => setOpenPattern((p) => ({ ...p, [key]: !p[key] }));

  const PatternSection = ({ sectionKey, title, children }) => {
    const open = !!openPattern[sectionKey];
    return (
      <div className="border-t border-[#EFE8DD] first:border-t-0">
        <button
          type="button"
          onClick={() => togglePattern(sectionKey)}
          className="w-full flex items-center justify-between py-4 text-left"
          data-testid={`pattern-toggle-${sectionKey}`}
        >
          <span className="font-display text-xl text-[#1F1B16]">{title}</span>
          {open ? <ChevronUp className="w-5 h-5 text-[#6F675D]" /> : <ChevronDown className="w-5 h-5 text-[#6F675D]" />}
        </button>
        {open && <div className="pb-5">{children}</div>}
      </div>
    );
  };

  const renderItems = (items) => (
    <div className="space-y-3">
      {(items || []).map((it, i) => (
        <p key={i} className="text-sm leading-relaxed text-[#5E574E]">
          <span className="font-semibold text-[#1F1B16]">{it.title}{it.title ? '.' : ''}</span>{' '}
          {it.body}
        </p>
      ))}
    </div>
  );

  const renderTimeline = (phases) => {
    const total = (phases || []).reduce((s, p) => s + parseSpan(p.time), 0) || 1;
    return (
      <div>
        <div className="flex gap-1.5 mb-4">
          {(phases || []).map((p, i) => (
            <div
              key={i}
              className="h-2.5 rounded-full"
              style={{ width: `${(parseSpan(p.time) / total) * 100}%`, backgroundColor: PHASE_COLOURS[i % PHASE_COLOURS.length] }}
            />
          ))}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {(phases || []).map((p, i) => (
            <div key={i}>
              <p className="text-xs text-[#9A9083] mb-0.5">{p.time}</p>
              <p className="font-semibold text-sm text-[#1F1B16]">{p.label}</p>
              <p className="text-xs text-[#6F675D] leading-snug mt-0.5">{p.description}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ── Numbered accordion shell (full width) ──────────────────────────────
  const Accordion = ({ sectionKey, num, title, badge, headerAction, children, testid }) => {
    const open = !!openSections[sectionKey];
    return (
      <Card className="bg-[#FFFDF9] border-[#EAE3D8] rounded-[20px] overflow-hidden" data-testid={testid}>
        <div
          role="button"
          tabIndex={0}
          onClick={() => toggleSection(sectionKey)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSection(sectionKey); } }}
          className="w-full flex items-center justify-between gap-3 p-5 cursor-pointer select-none"
          data-testid={`${testid}-header`}
        >
          <span className="flex items-center gap-2.5 min-w-0 flex-1">
            <span className="w-7 h-7 shrink-0 rounded-full bg-[#1F1B16] text-[#FFFDF9] text-sm flex items-center justify-center font-mono">{num}</span>
            <span className="font-display text-xl leading-tight text-[#1F1B16] break-words min-w-0">{title}</span>
            {badge != null && <span className="text-sm text-[#9A9083] shrink-0">{badge}</span>}
          </span>
          <span className="flex items-center gap-3 shrink-0">
            {headerAction && <span onClick={(e) => e.stopPropagation()}>{headerAction}</span>}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); toggleSection(sectionKey); }}
              className="w-8 h-8 -mr-1 rounded-full flex items-center justify-center text-[#6F675D] hover:bg-[#F3ECE1]"
              aria-label={open ? 'Collapse' : 'Expand'}
              data-testid={`${testid}-chevron`}
            >
              {open ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
          </span>
        </div>
        {open && <div className="px-5 pb-6">{children}</div>}
      </Card>
    );
  };

  // ── Editable feature / benefit pills (compact) ─────────────────────────
  const benefitPills = (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="folder-gen-benefits">
      {benefits.map((b, idx) => (
        editingBenefitIdx === idx ? (
          <Input
            key={idx}
            autoFocus
            value={editBenefitDraft}
            onChange={(e) => setEditBenefitDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitBenefitEdit(); } if (e.key === 'Escape') { setEditingBenefitIdx(null); setEditBenefitDraft(''); } }}
            onBlur={commitBenefitEdit}
            className="h-7 w-40 rounded-full bg-[#FFFDF9] border-[#E2D9CB] text-xs"
            data-testid={`folder-gen-benefit-edit-${idx}`}
          />
        ) : (
          <span key={idx} className="inline-flex items-center gap-1 h-7 pl-2.5 pr-1.5 rounded-full bg-[#FFFDF9] border border-[#E2D9CB] text-xs text-[#1F1B16]" data-testid={`folder-gen-benefit-${idx}`}>
            <button type="button" onClick={() => startEditBenefit(idx)} className="hover:text-primary" title="Edit benefit" data-testid={`folder-gen-benefit-editbtn-${idx}`}>
              {b}
            </button>
            <button type="button" onClick={() => removeBenefit(idx)} className="text-[#9A9083] hover:text-destructive" aria-label="Remove benefit" data-testid={`folder-gen-benefit-remove-${idx}`}>
              <X className="w-3 h-3" />
            </button>
          </span>
        )
      ))}
      {benefits.length < 5 && (
        addingBenefit ? (
          <Input
            autoFocus
            value={benefitDraft}
            onChange={(e) => setBenefitDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitBenefit(); } if (e.key === 'Escape') { setBenefitDraft(''); setAddingBenefit(false); } }}
            onBlur={commitBenefit}
            placeholder="Type a benefit…"
            className="h-7 w-36 rounded-full bg-[#FFFDF9] border-[#E2D9CB] text-xs"
            data-testid="folder-gen-benefit-input"
          />
        ) : (
          <button
            type="button"
            onClick={() => setAddingBenefit(true)}
            className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full border border-dashed border-[#D8CDBB] text-xs text-[#6F675D] hover:text-[#1F1B16] hover:border-[#C7BDB0]"
            data-testid="folder-gen-benefit-add"
          >
            <Plus className="w-3 h-3" /> Add benefit
          </button>
        )
      )}
    </div>
  );

  // Radio options for the Write-my-script section.
  const genOptions = isInstagram
    ? [
        { slug: 'winning', label: 'From viral scripts', hint: 'Mirror your extracted pattern' },
        { slug: 'tof', label: 'Top of funnel', hint: 'A complete short script' },
      ]
    : [
        { slug: 'winning', label: 'From viral scripts', hint: 'Mirror your extracted pattern' },
        { slug: 'bof', label: 'Simple BOF', hint: 'Just a 15-20s script — no hook/filming angles' },
        { slug: 'mof', label: 'MOF', hint: 'Middle of funnel — need → solution (~20s)' },
        { slug: 'tof', label: 'TOF', hint: 'Top of funnel — pain-led awareness (~30s)' },
      ];

  return (
    <AppShell title={folder.name}>
      <div className="max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between gap-3 mb-6">
          <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-[#6F675D] hover:text-primary" data-testid="folder-back">
            <ArrowLeft className="w-4 h-4" /> All products
          </Link>
          <span
            className={'inline-flex items-center text-[11px] font-medium px-2.5 py-1 rounded-full ' + (isInstagram ? 'bg-[#FBE4EE] text-[#9D174D]' : 'bg-[#DDF0F2] text-[#0B5566]')}
            data-testid="folder-platform-label"
          >
            {platformLabel}
          </span>
        </div>

        {/* ===== PRODUCT BANNER ===== */}
        <Card className="bg-[#FBF7F1] border-[#EAE3D8] rounded-[22px] p-6 mb-6" data-testid="product-banner">
          <div className="flex items-center justify-between gap-3 mb-5">
            <h2 className="flex items-center gap-2.5 font-display text-2xl text-[#1F1B16]">
              <span className="w-7 h-7 shrink-0 rounded-full bg-[#1F1B16] text-[#FFFDF9] text-sm flex items-center justify-center font-mono">1</span>
              Product
            </h2>
            {!isInstagram && (
              productUrl ? (
                <div className="flex items-center gap-2" data-testid="folder-product-link-row">
                  <a
                    href={productUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline underline-offset-4"
                    data-testid="folder-product-link"
                  >
                    View product on TikTok Shop <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                  <button
                    type="button"
                    onClick={resetProductUrl}
                    disabled={resettingUrl}
                    className="w-8 h-8 rounded-full flex items-center justify-center text-[#9A9083] hover:text-destructive hover:bg-[#F3ECE1] disabled:opacity-50"
                    title="Clear this link to import a different product"
                    data-testid="folder-product-link-reset"
                  >
                    {resettingUrl ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 bg-[#FFFDF9] border border-[#E2D9CB] rounded-full pl-3 pr-1 py-1 w-full max-w-md" data-testid="folder-import-block">
                  <Input
                    value={importUrl}
                    onChange={(e) => setImportUrl(e.target.value)}
                    placeholder="Paste a TikTok Shop product URL…"
                    disabled={importing}
                    className="h-8 border-0 bg-transparent shadow-none focus-visible:ring-0 px-0 text-sm"
                    data-testid="folder-import-input"
                  />
                  <button
                    type="button"
                    onClick={importProduct}
                    disabled={importing || !importUrl.trim()}
                    className="h-8 px-4 rounded-full bg-[#1F1B16] text-[#FFFDF9] text-sm font-medium flex items-center gap-1.5 disabled:opacity-50 shrink-0"
                    data-testid="folder-import-submit"
                  >
                    {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Import'}
                  </button>
                </div>
              )
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* LEFT: details */}
            <div className="lg:col-span-3 space-y-4">
              <div>
                <Label className="text-sm font-medium text-[#1F1B16] mb-1.5 block">
                  {isGrowth ? 'Problem or product name' : 'Product name'}
                  <span className="text-[#9A9083] font-normal ml-1">{productName.length}/{NAME_MAX}</span>
                </Label>
                <Input
                  value={productName}
                  maxLength={NAME_MAX}
                  onChange={(e) => setProductName(e.target.value.slice(0, NAME_MAX))}
                  placeholder={isGrowth ? 'e.g. Thinning hair, adult acne' : ''}
                  className="h-11 rounded-[12px] bg-[#FFFDF9] border-[#E2D9CB] font-medium"
                  data-testid="folder-gen-name"
                />
              </div>

              {isGrowth && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm font-medium text-[#1F1B16] mb-1.5 block">Hook ingredient</Label>
                    <Input value={hookIngredient} onChange={(e) => setHookIngredient(e.target.value)} placeholder="e.g. honey" className="h-11 rounded-[12px] bg-[#FFFDF9] border-[#E2D9CB]" data-testid="folder-gen-hook-ingredient" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-[#1F1B16] mb-1.5 block">Apply to</Label>
                    <Input value={appliedTo} onChange={(e) => setAppliedTo(e.target.value)} placeholder="e.g. hair" className="h-11 rounded-[12px] bg-[#FFFDF9] border-[#E2D9CB]" data-testid="folder-gen-applied-to" />
                  </div>
                </div>
              )}

              <div>
                <Label className="text-sm font-medium text-[#1F1B16] mb-1.5 block">
                  Description <span className="text-[#9A9083] font-normal">optional</span>
                </Label>
                <Textarea
                  value={productDescription}
                  onChange={(e) => setProductDescription(e.target.value)}
                  rows={3}
                  placeholder="What is this product? Paste or edit the description…"
                  className="rounded-[12px] bg-[#FFFDF9] border-[#E2D9CB] text-xs leading-relaxed"
                  data-testid="folder-gen-description"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-medium text-[#1F1B16] mb-1.5 block">{isGrowth ? 'Topic area' : 'Category'}</Label>
                  {isGrowth ? (
                    <Input value={productCategory} onChange={(e) => setProductCategory(e.target.value)} placeholder="e.g. hair" className="h-11 rounded-[12px] bg-[#FFFDF9] border-[#E2D9CB]" data-testid="folder-gen-category" />
                  ) : (
                    <Select value={productCategory} onValueChange={setProductCategory}>
                      <SelectTrigger className="h-11 rounded-[12px] bg-[#FFFDF9] border-[#E2D9CB]" data-testid="folder-gen-category-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {productCategories.map((cat) => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                {!isGrowth && (
                  <div>
                    <Label className="text-sm font-medium text-[#1F1B16] mb-1.5 block">Price <span className="text-[#9A9083] font-normal">optional</span></Label>
                    <Input value={productPrice} onChange={(e) => setProductPrice(e.target.value)} placeholder="£" className="h-11 rounded-[12px] bg-[#FFFDF9] border-[#E2D9CB]" data-testid="folder-gen-price" />
                  </div>
                )}
              </div>

              <div>
                <Label className="text-sm font-medium text-[#1F1B16] mb-1.5 block">Pain point <span className="text-[#9A9083] font-normal">optional</span></Label>
                <Input value={productPain} onChange={(e) => setProductPain(e.target.value)} placeholder={isInstagram ? 'e.g. dull skin' : 'e.g. stained teeth'} className="h-11 rounded-[12px] bg-[#FFFDF9] border-[#E2D9CB]" data-testid="folder-gen-pain" />
              </div>

              <div>
                <Label className="text-sm font-medium text-[#1F1B16] mb-1.5 block">
                  Features &amp; benefits <span className="text-[#9A9083] font-normal">{benefits.length}/5</span>
                </Label>
                {benefitPills}
                <p className="text-[11px] text-[#9A9083] mt-1.5">Tap a pill to edit it.</p>
              </div>
            </div>

            {/* RIGHT: images (main + thumbnails) */}
            <div className="lg:col-span-2">
              <Label className="text-sm font-medium text-[#1F1B16] mb-1.5 block">Images</Label>
              {productImages.length > 0 ? (
                <div data-testid="folder-product-gallery">
                  <button
                    type="button"
                    onClick={() => setLightboxSrc(mainImage)}
                    className="block w-full max-w-[180px] sm:max-w-[240px] lg:max-w-none mx-auto lg:mx-0 rounded-[14px] overflow-hidden border border-[#EAE3D8] hover:border-primary transition-colors"
                    data-testid="folder-product-image-main"
                    aria-label="View image larger"
                  >
                    <img src={mainImage} alt="" className="w-full aspect-square object-cover" />
                  </button>
                  {productImages.length > 1 && (
                    <div className="flex gap-2 mt-2 overflow-x-auto">
                      {productImages.slice(0, 5).map((src, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setActiveImageIdx(i)}
                          className={'shrink-0 rounded-lg overflow-hidden border transition-colors ' + (i === activeImageIdx ? 'border-primary' : 'border-[#EAE3D8] hover:border-[#C7BDB0]')}
                          data-testid={`folder-product-thumb-${i}`}
                        >
                          <img src={src} alt="" className="w-12 h-12 object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="rounded-[14px] border border-dashed border-[#D8CDBB] bg-[#FFFDF9] aspect-square flex items-center justify-center text-center p-6">
                  <p className="text-xs text-[#9A9083]">
                    {isInstagram ? 'Add images manually once supported.' : 'Import a TikTok Shop product to pull its images in here.'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* ===== 4 NUMBERED ACCORDIONS ===== */}
        <div className="space-y-4">
          {/* 1 — Viral scripts */}
          <Accordion
            sectionKey="winning"
            num={2}
            title="Viral scripts"
            testid="section-winning"
            badge={`${folder.transcripts?.length || 0} of ${transcriptLimit}`}
            headerAction={
              <Button
                variant="outline"
                size="sm"
                onClick={runAnalysis}
                disabled={analyzing || (folder.transcripts?.length || 0) < 2}
                title={(folder.transcripts?.length || 0) < 2 ? 'Add at least 2 videos first' : ''}
                data-testid="folder-analyze"
              >
                {analyzing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                {structured || folder.analysis_pattern ? 'Re-analyse' : 'Analyse'}
              </Button>
            }
          >
            <div className="space-y-2.5 mb-4">
              {(folder.transcripts || []).map((t) => (
                <div key={t.id} className="rounded-[14px] bg-[#FBF7F1] border border-[#EFE8DD]">
                  <div className="flex items-center gap-3 p-3">
                    {t.meta?.thumbnail ? (
                      <img src={t.meta.thumbnail} alt="" className="w-11 h-11 rounded-lg object-cover shrink-0" />
                    ) : (
                      <div className="w-11 h-11 rounded-lg bg-[#EAE0D2] shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-sm text-[#1F1B16] truncate">{videoHandle(t)}</p>
                      <p className="text-xs text-[#9A9083] truncate">{videoRef(t)}</p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <button
                        type="button"
                        onClick={() => setOpenTranscriptId(openTranscriptId === t.id ? null : t.id)}
                        className="text-sm text-[#1F1B16] underline underline-offset-4 hover:text-primary"
                        data-testid={`folder-transcript-link-${t.id}`}
                      >
                        Transcript
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteTranscript(t.id)}
                        className="text-[#B7B2A8] hover:text-destructive"
                        aria-label={`Remove ${videoHandle(t)}`}
                        data-testid={`folder-transcript-delete-${t.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  {openTranscriptId === t.id && (
                    <div className="px-3 pb-3">
                      <blockquote className="text-xs text-[#5E574E] leading-relaxed italic border-l-2 border-primary/30 pl-3 whitespace-pre-wrap">
                        {t.transcript}
                      </blockquote>
                      <div className="flex items-center gap-4 mt-2.5">
                        {t.url && (
                          <a href={t.url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-primary hover:underline inline-flex items-center gap-1">
                            <ExternalLink className="w-3 h-3" /> Open video
                          </a>
                        )}
                        <button
                          type="button"
                          onClick={() => runGenerate(true)}
                          disabled={generating}
                          className="text-[11px] text-primary hover:underline inline-flex items-center gap-1 disabled:opacity-60"
                          data-testid={`folder-spin-${t.id}`}
                        >
                          <Sparkles className="w-3 h-3" /> Spin into a script
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {!atTranscriptLimit ? (
              <>
                <form onSubmit={addTranscript} className="flex gap-2.5 items-start" data-testid="folder-add-transcript-form">
                  <Textarea
                    value={transcriptUrl}
                    onChange={(e) => setTranscriptUrl(e.target.value)}
                    placeholder={`Paste one or more ${linkNoun}s — one per line — then add them all at once`}
                    disabled={addingTranscript}
                    rows={3}
                    className="rounded-[12px] bg-[#FBF7F1] border-[#E2D9CB] text-sm"
                    data-testid="folder-add-transcript-input"
                  />
                  <button
                    type="submit"
                    disabled={addingTranscript || !transcriptUrl.trim()}
                    className="w-12 h-12 shrink-0 rounded-[12px] bg-[#1F1B16] text-[#FFFDF9] flex items-center justify-center hover:-translate-y-0.5 transition-transform disabled:opacity-50 disabled:translate-y-0"
                    title="Add all pasted links"
                    data-testid="folder-add-transcript-submit"
                  >
                    {addingTranscript ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
                  </button>
                </form>
                <p className="text-[11px] text-[#9A9083] mt-1.5">Add a few links at once, then hit <strong>Analyse</strong> above to analyse them together.</p>

                {!showManual ? (
                  <button
                    type="button"
                    onClick={() => setShowManual(true)}
                    className="mt-2.5 text-[13px] text-[#6F675D] hover:text-primary underline underline-offset-4"
                    data-testid="folder-manual-toggle"
                  >
                    …or paste a transcript instead
                  </button>
                ) : (
                  <div className="mt-3 space-y-2" data-testid="folder-manual-block">
                    <Textarea
                      value={manualText}
                      onChange={(e) => setManualText(e.target.value)}
                      rows={5}
                      placeholder="Paste the full transcript / script of a winning video here…"
                      className="rounded-[12px] bg-[#FBF7F1] border-[#E2D9CB]"
                      data-testid="folder-manual-input"
                    />
                    <div className="flex items-center gap-2">
                      <Button type="button" variant="premium" onClick={saveManualTranscript} disabled={savingManual || !manualText.trim()} data-testid="folder-manual-save">
                        {savingManual ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
                        Save transcript
                      </Button>
                      <button type="button" onClick={() => { setShowManual(false); setManualText(''); }} className="text-sm text-[#6F675D] hover:text-[#1F1B16]">
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-xs text-amber-600 flex items-center gap-1.5" data-testid="folder-transcript-limit">
                <AlertCircle className="w-3 h-3" />
                You&apos;ve hit the {transcriptLimit}-video limit. Remove one to add another, or upgrade for more.
              </p>
            )}
          </Accordion>

          {/* 3 — Top creator videos (TikTok only) */}
          {!isInstagram && (
            <Accordion
              sectionKey="creators"
              num={3}
              title="Top creator videos"
              testid="section-creators"
              badge={creatorVideos.length > 0 ? Math.min(creatorVideos.length, 3) : null}
              headerAction={
                <div className="flex flex-wrap items-center gap-2">
                  {folder.kalo_product_id && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); analyzeCreatorScripts(); }}
                      disabled={analyzingCreators || atTranscriptLimit}
                      title={atTranscriptLimit ? 'Viral-scripts limit reached' : 'Pull this product’s top 3 videos, transcribe them and add them as viral scripts'}
                      data-testid="creator-videos-analyze"
                    >
                      {analyzingCreators ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                      Auto-pull top 3 videos
                    </Button>
                  )}
                  {creatorVideos.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); pullTopCreatorVideos(); }}
                      disabled={usingTopCreators || atTranscriptLimit}
                      title={atTranscriptLimit ? 'Viral-scripts limit reached' : 'Transcribe the top 3 creator videos and add them as viral scripts'}
                      data-testid="creator-videos-use-top-3"
                    >
                      {usingTopCreators ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1.5" />}
                      Use top 3 videos
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); findCreatorVideos(); }}
                    disabled={findingCreators || !productUrl}
                    title={!productUrl ? 'Import a TikTok Shop product first' : 'Pull the product’s top creator videos'}
                    data-testid="creator-videos-find"
                  >
                    {findingCreators ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                    {creatorVideos.length > 0 ? 'Refresh' : 'Find creator videos'}
                  </Button>
                </div>
              }
            >
              {creatorError && (
                <p className="text-xs text-red-600 mb-2 truncate" title={creatorError} data-testid="creator-videos-error">{creatorError}</p>
              )}
              <p className="text-[13px] text-[#6F675D] mb-4">
                {productUrl
                  ? 'Real creators already promoting this product. Pull one in as inspiration and it feeds your winning pattern.'
                  : 'Import a TikTok Shop product above first, then find the creators already selling it.'}
              </p>
              {creatorVideos.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5" data-testid="creator-videos-list">
                  {creatorVideos.slice(0, 3).map((v, i) => {
                    const already = addedVideoIds.has(String(v.url)) || (v.video_id && addedVideoIds.has(`video/${String(v.video_id).slice(0, 10)}…`));
                    const views = fmtCount(v.views);
                    return (
                      <div key={i} className="rounded-[14px] bg-[#FBF7F1] border border-[#EFE8DD] p-3 flex items-center gap-3" data-testid={`creator-video-${i}`}>
                        {v.thumbnail ? (
                          <img src={v.thumbnail} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />
                        ) : (
                          <div className="w-12 h-12 rounded-lg bg-[#EAE0D2] shrink-0" />
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-sm text-[#1F1B16] truncate">{v.handle ? `@${v.handle}` : (v.display_name || 'Creator')}</p>
                          <p className="text-xs text-[#9A9083] truncate">{views ? `${views} views` : (v.display_name || 'TikTok video')}</p>
                          <div className="flex items-center gap-3 mt-1.5">
                            {v.url && (
                              <a href={v.url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-[#6F675D] hover:text-primary inline-flex items-center gap-1">
                                <ExternalLink className="w-3 h-3" /> Watch
                              </a>
                            )}
                            {already ? (
                              <span className="text-[11px] text-[#0B5566] inline-flex items-center gap-1">Added</span>
                            ) : (
                              <button
                                type="button"
                                onClick={() => addCreatorVideoAsInspiration(v.url)}
                                disabled={!!usingCreator || atTranscriptLimit}
                                title={atTranscriptLimit ? 'Viral-scripts limit reached' : ''}
                                className="text-[11px] text-primary hover:underline inline-flex items-center gap-1 disabled:opacity-50"
                                data-testid={`creator-video-use-${i}`}
                              >
                                {usingCreator === v.url ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                                Use as inspiration
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Accordion>
          )}

          {/* Winning pattern — standalone, under Top creator videos (no number) */}
          <div className="rounded-[18px] border border-[#EFE8DD] bg-white p-5 sm:p-6" data-testid="section-pattern">
            <div className="flex items-center justify-between gap-3 mb-1">
              <h3 className="font-display text-xl text-[#1F1B16]">Winning pattern</h3>
              {(structured || folder.analysis_pattern) && (
                <span className="text-sm text-[#9A9083]">From {patternVideoCount} videos</span>
              )}
            </div>
            {structured ? (
              <div>
                <PatternSection sectionKey="hook_style" title="Hook style">{renderItems(structured.hook_style)}</PatternSection>
                <PatternSection sectionKey="structure" title="Structure & pacing">{renderTimeline(structured.structure)}</PatternSection>
                <PatternSection sectionKey="tone_voice" title="Tone & voice">{renderItems(structured.tone_voice)}</PatternSection>
                <PatternSection sectionKey="persuasion_levers" title="Persuasion levers">{renderItems(structured.persuasion_levers)}</PatternSection>
                <PatternSection sectionKey="copy_pattern" title="Copy pattern">{renderItems(structured.copy_pattern)}</PatternSection>
                {folder.analysis_stale && (
                  <p className="text-[11px] text-amber-600 flex items-center gap-1 mt-3">
                    <AlertCircle className="w-3 h-3" /> Videos changed — re-analyse for a fresh pattern
                  </p>
                )}
              </div>
            ) : folder.analysis_pattern ? (
              <div className="prose prose-sm max-w-none text-sm leading-relaxed mt-3" data-testid="folder-analysis-content">
                <ReactMarkdown>{folder.analysis_pattern}</ReactMarkdown>
              </div>
            ) : (
              <p className="text-sm text-[#6F675D] mt-2" data-testid="folder-analysis-empty">
                Add at least 2 <strong>viral scripts</strong> above and hit <strong>Analyse</strong>{folder.kalo_product_id ? <>, or tap <strong>Auto-pull top 3 videos</strong> on Top creator videos</> : null} to extract the shared hook, structure and pacing. Pick <strong>From viral scripts</strong> in Create script to write from it.
              </p>
            )}
          </div>

          {/* 3 — Create script */}
          <Accordion sectionKey="write" num={4} title="Create script" testid="section-write">
            {/* Required choice (radios) — above Tone */}
            <p className="text-[11px] font-mono uppercase tracking-wider text-[#9A9083] mb-2">Choose a script type</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="folder-genmode-group" role="radiogroup">
              {genOptions.map((o) => {
                const disabled = o.slug === 'winning' && !hasTranscripts;
                const selected = genMode === o.slug;
                return (
                  <button
                    key={o.slug}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    disabled={disabled}
                    onClick={() => setGenMode(o.slug)}
                    title={disabled ? 'Add viral scripts first' : ''}
                    className={
                      'flex items-start gap-3 text-left rounded-[14px] border p-3 transition-colors disabled:opacity-50 ' +
                      (selected ? 'border-primary bg-[#FFF5F9]' : 'border-[#E2D9CB] bg-[#FBF7F1] hover:border-[#C7BDB0]')
                    }
                    data-testid={`folder-genmode-${o.slug}`}
                  >
                    <span className={'mt-0.5 w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center ' + (selected ? 'border-primary' : 'border-[#C7BDB0]')}>
                      {selected && <span className="w-2 h-2 rounded-full bg-primary" />}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-[#1F1B16]">{o.label}</span>
                      <span className="block text-xs text-[#6F675D]">{o.hint}</span>
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Tone */}
            <p className="text-[11px] font-mono uppercase tracking-wider text-[#9A9083] mt-5 mb-2">Tone</p>
            <div className="grid grid-cols-3 gap-1 rounded-full bg-[#F1E9DD] p-1 sm:max-w-md" data-testid="folder-script-style-switch">
              {[{ slug: 'ugc', label: 'UGC creator' }, { slug: 'authority', label: 'Authority' }, { slug: 'negative', label: 'Negative' }].map((s) => (
                <button
                  key={s.slug}
                  type="button"
                  onClick={() => setScriptStyle(s.slug)}
                  className={'h-9 rounded-full text-sm font-medium transition-colors ' + (scriptStyle === s.slug ? 'bg-[#FFFDF9] text-[#1F1B16] shadow-sm' : 'text-[#6F675D] hover:text-[#1F1B16]')}
                  data-testid={`folder-script-style-${s.slug}`}
                >
                  {s.label}
                </button>
              ))}
            </div>

            {isInstagram && (
              <div className="mt-4">
                <p className="text-[11px] font-mono uppercase tracking-wider text-[#9A9083] mb-2">Video type</p>
                <div className="inline-flex rounded-full bg-[#F1E9DD] p-1 text-sm" data-testid="folder-video-category-switch">
                  {[{ slug: 'sales', label: 'Sales' }, { slug: 'growth', label: 'Growth · Preview' }].map((c) => (
                    <button
                      key={c.slug}
                      type="button"
                      onClick={() => setVideoCategory(c.slug)}
                      className={'px-4 h-8 rounded-full font-medium transition-colors ' + (videoCategory === c.slug ? 'bg-[#FFFDF9] text-[#1F1B16] shadow-sm' : 'text-[#6F675D]')}
                      data-testid={`folder-video-category-${c.slug}`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
            )}


            {/* Optional AI guidance */}
            <div className="mt-4" data-testid="folder-guidance">
              <button
                type="button"
                onClick={() => setShowGuidance((s) => !s)}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                data-testid="folder-guidance-toggle"
              >
                <Sparkles className="w-4 h-4" />
                {scriptGuidance.trim() ? 'Edit guidance for the AI' : 'Add guidance for the AI (optional)'}
              </button>
              {showGuidance && (
                <div className="mt-2">
                  <Textarea
                    value={scriptGuidance}
                    onChange={(e) => setScriptGuidance(e.target.value)}
                    rows={3}
                    maxLength={500}
                    placeholder="Give the AI extra direction, e.g. 'Write it as a calm voiceover narration', 'Keep it playful and fast-paced', 'Mention limited stock'…"
                    className="bg-[#FBF7F1] border-[#E2D9CB] text-[#1F1B16] text-sm"
                    data-testid="folder-guidance-input"
                  />
                  <p className="text-[11px] text-[#9A9083] mt-1">Passed to the AI as extra direction for your script. {scriptGuidance.length}/500</p>
                </div>
              )}
              {!showGuidance && scriptGuidance.trim() && (
                <p className="text-xs text-[#6F675D] mt-1 line-clamp-1" data-testid="folder-guidance-preview">Guidance: “{scriptGuidance.trim()}”</p>
              )}
            </div>

            <button
              type="button"
              onClick={handleWriteScript}
              disabled={generating || !productName.trim()}
              className="mt-5 w-full sm:w-auto h-14 px-10 rounded-[14px] bg-[#BE185D] text-white text-base font-semibold inline-flex items-center justify-center gap-2 hover:-translate-y-0.5 transition-transform disabled:opacity-60 disabled:translate-y-0 shadow-sm"
              data-testid="folder-write-script"
            >
              {generating ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Create Script <ArrowRight className="w-5 h-5" /></>}
            </button>
            {!productName.trim() && <p className="text-xs text-[#9A9083] mt-2">Give your product a name above to enable this.</p>}
            {genMode === 'bof' && (
              <p className="text-xs text-[#6F675D] mt-3" data-testid="folder-bof-note">
                Simple BOF is just a 15-20s script — no hooks or filming angles. If you need those, choose <strong>MOF</strong> or <strong>TOF</strong>.
              </p>
            )}
          </Accordion>

          {/* 4 — Generate voiceover */}
          <Accordion sectionKey="voiceover" num={5} title="Generate voiceover" testid="section-voiceover">
            <p className="text-[13px] text-[#6F675D] mb-3">Paste a script, or load one from your history, then turn it into a voiceover.</p>
            {(folder.generations || []).length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3" data-testid="voiceover-history-picker">
                {folder.generations.slice(0, 5).map((g) => (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() => setVoiceoverText(spokenClean(g))}
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-[#E2D9CB] bg-[#FBF7F1] text-xs text-[#1F1B16] hover:border-[#C7BDB0]"
                    data-testid={`voiceover-load-${g.id}`}
                  >
                    <Sparkles className="w-3 h-3 text-primary" /> {g.product_name || 'Untitled'}
                  </button>
                ))}
              </div>
            )}
            <Textarea
              value={voiceoverText}
              onChange={(e) => setVoiceoverText(e.target.value)}
              rows={5}
              placeholder="Paste the script you want voiced here…"
              className="rounded-[12px] bg-[#FBF7F1] border-[#E2D9CB] text-sm"
              data-testid="voiceover-text"
            />
            <div data-testid="voiceover-section-buttons">
              <VoiceoverButton scriptText={voiceoverText.trim()} testidPrefix="voiceover-section" defaultOpen />
            </div>

            {/* Affiliate sign-up links (small hyperlinks) */}
            <div className="mt-4 pt-4 border-t border-[#EFE8DD]" data-testid="voiceover-affiliates">
              <p className="text-[12px] text-[#6F675D]">
                Want more voices? Use your own account —{' '}
                <a href={voiceAffiliates.elevenlabs || 'https://elevenlabs.io'} target="_blank" rel="sponsored noopener noreferrer" className="text-primary hover:underline" data-testid="voiceover-aff-elevenlabs">ElevenLabs</a>
                {' '}or{' '}
                <a href={voiceAffiliates.fish || 'https://fish.audio'} target="_blank" rel="sponsored noopener noreferrer" className="text-primary hover:underline" data-testid="voiceover-aff-fish">Fish Audio</a>.
              </p>
              <p className="text-[11px] text-[#9A9083] mt-1" data-testid="voiceover-aff-disclosure">
                We may earn a commission if you sign up through these links.
              </p>
            </div>
          </Accordion>

          {/* 6 — History */}
          <Accordion
            sectionKey="history"
            num={6}
            title="History"
            testid="section-history"
            badge={(folder.generations || []).length || null}
          >
            {(folder.generations || []).length === 0 ? (
              <p className="text-sm text-[#6F675D]">No scripts yet — your first one will appear here.</p>
            ) : (
              <div>
                {folder.generations.slice(0, 5).map((g) => (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() => setActiveGen(g)}
                    className="w-full flex items-center justify-between gap-3 py-3 border-t border-[#EFE8DD] first:border-t-0 text-left hover:text-primary transition-colors"
                    data-testid={`folder-generation-${g.id}`}
                  >
                    <span className="truncate text-[#1F1B16]">{g.product_name || 'Untitled'}</span>
                    <span className="text-sm text-[#9A9083] shrink-0">
                      {g.generated_at ? new Date(g.generated_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                    </span>
                  </button>
                ))}
                {folder.generations.length > 5 && (
                  <p className="text-[11px] text-[#9A9083] mt-3" data-testid="folder-history-limit-note">Showing your 5 most recent scripts.</p>
                )}
              </div>
            )}
          </Accordion>
        </div>
      </div>

      {/* Generated-script popup */}
      <Dialog open={!!activeGen} onOpenChange={(o) => { if (!o) setActiveGen(null); }}>
        <DialogContent className="max-w-2xl max-h-[86vh] overflow-y-auto bg-[#FFFDF9]" data-testid="script-modal">
          <DialogTitle className="sr-only">Generated script</DialogTitle>
          {activeGen && (() => {
            const g = activeGen;
            const isScenes = g.scenes && g.scenes.length > 0;
            const spoken = spokenClean(g);
            const extraFilming = filmingByGen[g.id];
            return (
              <div>
                <div className="flex items-center justify-between gap-3 mb-3">
                  <button
                    type="button"
                    onClick={() => setActiveGen(null)}
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-[10px] bg-[#1F1B16] text-[#FFFDF9] text-sm font-medium hover:-translate-y-0.5 transition-transform"
                    data-testid="script-modal-close"
                  >
                    <X className="w-4 h-4" /> Close window
                  </button>
                  <button
                    type="button"
                    onClick={() => runGenerate((lastFlavour || (hasTranscripts ? 'inspired' : 'fresh')) === 'inspired', lastScriptType)}
                    disabled={generating || !productName.trim()}
                    className="shrink-0 inline-flex items-center gap-1.5 h-9 px-3 rounded-[10px] border border-[#E2D9CB] text-sm text-[#1F1B16] hover:bg-[#FBF7F1] disabled:opacity-60"
                    title="Generate a fresh variation"
                    data-testid="script-modal-regenerate"
                  >
                    {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    {generating ? 'Regenerating…' : 'Regenerate'}
                  </button>
                </div>
                <div className="mb-4">
                  <h3 className="font-display text-2xl text-[#1F1B16]">{g.product_name || 'Your script'}</h3>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {[g.platform, g.script_style, g.video_category, g.script_type && (g.script_type === 'bof' ? 'Simple BOF' : g.script_type === 'mof' ? 'MOF' : g.script_type === 'tof' ? 'TOF' : 'General')].filter(Boolean).map((tag, i) => (
                      <span key={i} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#F1E9DD] text-[#6F675D]">{tag}</span>
                    ))}
                  </div>
                </div>

                <div className="rounded-[14px] bg-[#FBF7F1] border border-[#EFE8DD] p-4 mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[11px] font-mono uppercase tracking-wider text-[#9A9083]">Script</p>
                    <button type="button" onClick={() => copyToClipboard(spoken)} className="text-[11px] text-primary hover:underline inline-flex items-center gap-1" data-testid="script-modal-copy">
                      <Copy className="w-3 h-3" /> Copy script
                    </button>
                  </div>
                  <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-[#1F1B16]">{spoken}</pre>
                </div>

                {g.script_type === 'bof' && (
                  <p className="text-xs text-[#6F675D] mb-4" data-testid="script-modal-bof-note">
                    This is a Simple BOF script. If you need hook angles, change the script type to <strong>MOF</strong> or <strong>TOF</strong>. Voiceovers are in the "Generate voiceover" section.
                  </p>
                )}

                {isScenes ? (
                  <div>
                    <p className="text-[11px] font-mono uppercase tracking-wider text-[#9A9083] mb-2">Scene cuts</p>
                    <ol className="space-y-2 list-none">
                      {g.scenes.map((sc, i) => (
                        <li key={i} className="rounded-md bg-[#F6F0E7] p-2.5 text-xs leading-relaxed">
                          <p className="font-mono uppercase tracking-wider text-[10px] text-primary mb-1">Scene {i + 1} — {sc.label}</p>
                          <p className="mb-1"><span className="text-[#9A9083]">Visual:</span> {sc.visual_direction}</p>
                          <p className="mb-1"><span className="text-[#9A9083]">Line:</span> &ldquo;{sc.dialogue}&rdquo;</p>
                          {sc.text_overlay && <p className="mb-1"><span className="text-[#9A9083]">Overlay:</span> {sc.text_overlay}</p>}
                          {sc.transition && <p className="text-[#9A9083] italic">→ {sc.transition}</p>}
                        </li>
                      ))}
                    </ol>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {g.text_hooks?.length > 0 && (
                      <div>
                        <p className="text-[11px] font-mono uppercase tracking-wider text-[#9A9083] mb-1">Hooks</p>
                        <ul className="space-y-1 list-disc list-inside text-sm">{g.text_hooks.map((h, i) => <li key={i}>{h}</li>)}</ul>
                      </div>
                    )}
                    {g.visual_hooks?.length > 0 && (
                      <div>
                        <p className="text-[11px] font-mono uppercase tracking-wider text-[#9A9083] mb-1">Visual hooks</p>
                        <ul className="space-y-1.5 text-sm">{g.visual_hooks.map((v, i) => (
                          <li key={i}><p className="font-medium">{v.hook}</p>{v.why_it_works && <p className="text-[11px] text-[#9A9083] mt-0.5">Why: {v.why_it_works}</p>}</li>
                        ))}</ul>
                      </div>
                    )}
                    {g.verbal_hooks?.length > 0 && (
                      <div>
                        <p className="text-[11px] font-mono uppercase tracking-wider text-[#9A9083] mb-1">Verbal hooks</p>
                        <ul className="space-y-1 list-disc list-inside text-sm">{g.verbal_hooks.map((h, i) => <li key={i}>{h}</li>)}</ul>
                      </div>
                    )}
                    {(g.filming_ideas?.length > 0 || extraFilming?.length > 0) ? (
                      <div>
                        <p className="text-[11px] font-mono uppercase tracking-wider text-[#9A9083] mb-1">Filming angles</p>
                        <ul className="space-y-1 list-disc list-inside text-sm">{(extraFilming || g.filming_ideas).map((f, i) => <li key={i}>{f}</li>)}</ul>
                      </div>
                    ) : (!isScenes && g.script_type !== 'bof' && (
                      <button
                        type="button"
                        onClick={() => generateFilming(g)}
                        disabled={filmingLoading}
                        className="inline-flex items-center gap-1.5 h-10 px-4 rounded-[10px] border border-[#E2D9CB] bg-[#FBF7F1] text-sm font-medium text-[#1F1B16] hover:border-[#C7BDB0] disabled:opacity-60"
                        data-testid="script-modal-generate-filming"
                      >
                        {filmingLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clapperboard className="w-4 h-4" />}
                        {filmingLoading ? 'Generating…' : 'Generate filming angles'}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Image lightbox */}
      <Dialog open={!!lightboxSrc} onOpenChange={(o) => { if (!o) setLightboxSrc(null); }}>
        <DialogContent className="max-w-3xl bg-[#FFFDF9] p-2" data-testid="image-lightbox">
          <DialogTitle className="sr-only">Product image</DialogTitle>
          {lightboxSrc && <img src={lightboxSrc} alt="" className="w-full max-h-[80vh] object-contain rounded-[12px]" />}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
};

export default FolderDetailPage;
